<template>
  <div class="reg-panel">
    <div class="panel-body">
      <div class="row g-3 align-items-start">
        <!-- Columna izquierda -->
        <div class="col-8">

          <!-- 1. Carga de CSV -->
          <div class="search-box">
            <label class="field-label">Archivo CSV (columna "numero" con los N° de planilla)</label>
            <div class="d-flex gap-1">
              <input
                ref="inputCsv"
                type="file"
                accept=".csv"
                class="search-input"
                @change="onCsvChange"
              />
              <button
                type="button"
                class="btn-reload"
                @click="$refs.inputCsv.click()"
                title="Seleccionar CSV"
              >
                <span class="material-symbols-outlined" style="font-size:18px">upload_file</span>
              </button>
            </div>
            <div class="search-status mt-1">
              <span v-if="archivoCsv" class="text-muted">
                <span class="material-symbols-outlined" style="font-size:14px; vertical-align:-2px">description</span>
                {{ archivoCsv.name }}
                <span v-if="numerosCsv.length"> — {{ numerosCsv.length }} número(s) detectado(s)</span>
              </span>
              <span v-else class="text-muted">Ningún archivo seleccionado</span>
            </div>
          </div>

          <!-- 2. Carga de PDFs -->
          <div class="search-box mt-3">
            <label class="field-label">Archivos PDF a cargar en cada planilla</label>
            <div class="d-flex gap-1">
              <input
                ref="inputPdf"
                type="file"
                accept="application/pdf,.pdf"
                multiple
                class="search-input"
                @change="onPdfChange"
              />
              <button
                type="button"
                class="btn-reload"
                @click="$refs.inputPdf.click()"
                title="Seleccionar PDFs"
              >
                <span class="material-symbols-outlined" style="font-size:18px">picture_as_pdf</span>
              </button>
            </div>
            <div class="search-status mt-1">
              <span v-if="pdfs.length" class="text-muted">
                {{ pdfs.length }} PDF(s) — {{ formatearTamano(tamanoTotalPdfs) }}
              </span>
              <span v-else class="text-muted">Ningún PDF seleccionado</span>
            </div>
            <div v-if="pdfs.length" class="lista-pdfs mt-1">
              <span
                v-for="(f, i) in pdfs"
                :key="i"
                class="pdf-chip"
              >
                <span class="material-symbols-outlined" style="font-size:13px">picture_as_pdf</span>
                {{ f.name }}
                <span class="pdf-chip-quitar" @click="quitarPdf(i)" title="Quitar">×</span>
              </span>
            </div>
          </div>

          <!-- Resultado de la resolución numero -> idPlanilla -->
          <div v-if="resolucion.length" class="tabla-seleccionados mt-3">
            <div class="tabla-header" style="grid-template-columns: 1fr 1fr auto;">
              <span>Número</span>
              <span>idPlanilla</span>
              <span>Estado</span>
            </div>
            <div class="log-box">
              <div
                v-for="(r, i) in resolucion"
                :key="i"
                class="log-line d-flex align-items-center justify-content-between"
              >
                <span>{{ r.numero }}</span>
                <span>{{ r.idPlanilla ?? "—" }}</span>
                <span :class="claseEstado(r.estado)">{{ etiquetaEstado(r.estado) }}</span>
              </div>
            </div>
          </div>

          <div v-if="!archivoCsv" class="tabla-vacia mt-3">
            <span class="material-symbols-outlined" style="font-size:28px; color:#ced4da">upload_file</span>
            <p class="mt-1 mb-0">Seleccione un CSV y los PDF a cargar</p>
          </div>
        </div>

        <!-- Columna derecha: acciones -->
        <div class="col-4 d-flex flex-column gap-3" style="padding-top:2rem;">
          <div class="resumen-box">
            <div class="resumen-row">
              <span>Trámite</span>
              <strong class="text-primary">{{ idTramite }}</strong>
            </div>
            <div class="resumen-row" v-if="numerosCsv.length">
              <span>Números en CSV</span>
              <strong>{{ numerosCsv.length }}</strong>
            </div>
            <div class="resumen-row" v-if="resolucion.length">
              <span>Resueltos</span>
              <strong class="text-success">{{ totalResueltos }}</strong>
            </div>
            <div class="resumen-row" v-if="resolucion.length">
              <span>No encontrados</span>
              <strong :class="totalNoEncontrados > 0 ? 'text-danger' : ''">{{ totalNoEncontrados }}</strong>
            </div>
            <div class="resumen-row" v-if="pdfs.length">
              <span>PDF por planilla</span>
              <strong>{{ pdfs.length }}</strong>
            </div>
            <div class="resumen-row" v-if="cargados !== null">
              <span>Planillas cargadas</span>
              <strong class="text-success">{{ cargados }}</strong>
            </div>
            <div class="resumen-row" v-if="fallidas !== null">
              <span>Planillas con error</span>
              <strong :class="fallidas > 0 ? 'text-danger' : ''">{{ fallidas }}</strong>
            </div>
            <div v-if="procesando" class="resumen-row">
              <span>Progreso</span>
              <strong>{{ progresoActual }} / {{ totalResueltos }}</strong>
            </div>
          </div>

          <button
            type="button" class="btn-action secondary"
            @click="resolverPlanillas"
            :disabled="!archivoCsv || resolviendo || procesando"
          >
            <span class="material-symbols-outlined">search</span>
            {{ resolviendo ? "Resolviendo..." : "Resolver planillas" }}
          </button>

          <button
            type="button" class="btn-action primary"
            @click="confirmarCarga"
            :disabled="!puedeCargar"
          >
            <span class="material-symbols-outlined">upload_file</span>
            {{ procesando ? `Cargando... ${progresoActual}/${totalResueltos}` : "Cargar a todas las planillas" }}
          </button>

          <button
            type="button" class="btn-action secondary"
            @click="limpiar"
            :disabled="procesando"
          >
            <span class="material-symbols-outlined">delete</span>
            Limpiar
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from "@/services/api";
import Swal from "sweetalert2";

function extraerColumnaCSV(texto, nombreColumna) {
  const filas = texto
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0);

  if (filas.length === 0) return [];

  const encabezados = filas[0].split(",").map((h) => h.trim().toLowerCase());
  const idx = encabezados.indexOf(nombreColumna.toLowerCase());
  if (idx === -1) {
    throw new Error(`No se encontró la columna "${nombreColumna}" en el CSV`);
  }

  return filas
    .slice(1)
    .map((fila) => (fila.split(",")[idx] || "").trim())
    .filter((v) => v.length > 0);
}

// Estados posibles de cada fila resuelta/procesada
const ESTADOS = {
  PENDIENTE: "pendiente",
  RESUELTO: "resuelto",
  NO_ENCONTRADO: "no_encontrado",
  CARGANDO: "cargando",
  CARGADO: "cargado",
  ERROR: "error",
};

export default {
  name: "CargarArchivosPlaCSV",
  props: {
    idTramite: { type: [Number, String], required: true },
    idUsuario: { type: [Number, String], required: true },
    idCarga: { type: [Number, String], default: null },
  },
  emits: ["ejecutado", "cargado"],
  data() {
    return {
      archivoCsv: null,
      numerosCsv: [],
      pdfs: [],
      resolucion: [],   // [{ numero, idPlanilla, estado, mensaje }]
      resolviendo: false,
      procesando: false,
      progresoActual: 0,
      cargados: null,
      fallidas: null,
      TAMANO_LOTE: 5, // planillas procesadas en paralelo durante la carga
    };
  },
  computed: {
    tamanoTotalPdfs() {
      return this.pdfs.reduce((acc, f) => acc + f.size, 0);
    },
    totalResueltos() {
      return this.resolucion.filter((r) => r.estado !== ESTADOS.NO_ENCONTRADO).length;
    },
    totalNoEncontrados() {
      return this.resolucion.filter((r) => r.estado === ESTADOS.NO_ENCONTRADO).length;
    },
    puedeCargar() {
      return (
        this.resolucion.length > 0 &&
        this.totalResueltos > 0 &&
        this.totalNoEncontrados === 0 &&
        this.pdfs.length > 0 &&
        !this.procesando &&
        !this.resolviendo
      );
    },
  },
  methods: {
    // ---------- CSV ----------
    onCsvChange(e) {
      const file = e.target.files[0] || null;
      this.archivoCsv = file;
      this.resolucion = [];
      this.numerosCsv = [];
      this.cargados = null;
      this.fallidas = null;
      if (file) this.leerNumeros(file);
    },

    leerNumeros(file) {
      const reader = new FileReader();
      reader.onload = () => {
        try {
          const texto = String(reader.result || "");
          this.numerosCsv = extraerColumnaCSV(texto, "numero");
        } catch (err) {
          Swal.fire("Error en el CSV", err.message, "error");
          this.numerosCsv = [];
        }
      };
      reader.readAsText(file, "ISO-8859-1");
    },

    // ---------- PDFs ----------
    onPdfChange(e) {
      const nuevos = Array.from(e.target.files || []);
      this.agregarPdfs(nuevos);
      e.target.value = "";
    },

    agregarPdfs(nuevos) {
      const rechazados = [];
      nuevos.forEach((file) => {
        const esPDF = file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
        if (!esPDF) {
          rechazados.push(file.name);
          return;
        }
        const yaExiste = this.pdfs.some((f) => f.name === file.name && f.size === file.size);
        if (!yaExiste) this.pdfs.push(file);
      });
      if (rechazados.length) {
        Swal.fire({
          icon: "warning",
          title: "Archivos ignorados",
          text: `Solo se permiten PDF. Se ignoraron: ${rechazados.join(", ")}`,
          confirmButtonColor: "#f39c12",
        });
      }
    },

    quitarPdf(index) {
      this.pdfs.splice(index, 1);
    },

    formatearTamano(bytes) {
      if (bytes < 1024) return bytes + " B";
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
      return (bytes / (1024 * 1024)).toFixed(1) + " MB";
    },

    // ---------- Resolución numero -> idPlanilla ----------
    async resolverPlanillas() {
      if (this.numerosCsv.length === 0) {
        Swal.fire("Atención", "No hay números para resolver", "warning");
        return;
      }

      this.resolviendo = true;
      this.resolucion = [];
      this.cargados = null;
      this.fallidas = null;

      const numerosUnicos = [...new Set(this.numerosCsv)];
      const LOTE = 10;

      try {
        for (let i = 0; i < numerosUnicos.length; i += LOTE) {
          const lote = numerosUnicos.slice(i, i + LOTE);
          const resultados = await Promise.all(lote.map((n) => this.resolverUnNumero(n)));
          this.resolucion.push(...resultados);
        }

        if (this.totalNoEncontrados > 0) {
          Swal.fire(
            "Resolución con pendientes",
            `${this.totalNoEncontrados} número(s) no se encontraron en el trámite ${this.idTramite}. Corrija el CSV antes de continuar.`,
            "warning"
          );
        } else {
          Swal.fire("Resolución completa", `${this.totalResueltos} planilla(s) identificada(s) correctamente.`, "success");
        }
      } finally {
        this.resolviendo = false;
      }
    },

    async resolverUnNumero(numero) {
      try {
        const { data } = await api.get(`/vwplanillas/numero/${this.idTramite}/${numero}`);
        const item = Array.isArray(data) ? data[0] : data;
        if (item && item.idPlanilla) {
          return { numero, idPlanilla: item.idPlanilla, estado: ESTADOS.RESUELTO };
        }
        return { numero, idPlanilla: null, estado: ESTADOS.NO_ENCONTRADO };
      } catch (err) {
        return { numero, idPlanilla: null, estado: ESTADOS.NO_ENCONTRADO };
      }
    },

    // ---------- Carga de PDFs a cada planilla ----------
    async confirmarCarga() {
      const confirm = await Swal.fire({
        title: "¿Cargar archivos a todas las planillas?",
        html: `Se subirán <strong>${this.pdfs.length}</strong> archivo(s) PDF a
               <strong>${this.totalResueltos}</strong> planilla(s) del trámite
               <strong>${this.idTramite}</strong>.`,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Sí, cargar",
        cancelButtonText: "Cancelar",
        confirmButtonColor: "#0d6efd",
      });
      if (!confirm.isConfirmed) return;
      await this.cargarATodasLasPlanillas();
    },

    async cargarUnaPlanilla(item) {
      item.estado = ESTADOS.CARGANDO;
      try {
        const formData = new FormData();
        this.pdfs.forEach((file) => formData.append("files", file)); // mismos File, reutilizados
        formData.append("idTramite", Number(this.idTramite));
        formData.append("idPlanilla", Number(item.idPlanilla));
        if (this.idCarga) formData.append("idCarga", Number(this.idCarga));

        await api.post("/archivospla/upload-multiple", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        item.estado = ESTADOS.CARGADO;
      } catch (err) {
        item.estado = ESTADOS.ERROR;
        const raw = err.response?.data;
        item.mensaje = typeof raw === "string" ? raw : raw?.message ?? raw?.error ?? "Error al cargar";
      } finally {
        this.progresoActual++;
      }
    },

    async cargarATodasLasPlanillas() {
      const pendientes = this.resolucion.filter((r) => r.estado === ESTADOS.RESUELTO);
      if (pendientes.length === 0) return;

      this.procesando = true;
      this.progresoActual = 0;

      try {
        for (let i = 0; i < pendientes.length; i += this.TAMANO_LOTE) {
          const lote = pendientes.slice(i, i + this.TAMANO_LOTE);
          await Promise.all(lote.map((item) => this.cargarUnaPlanilla(item)));
        }

        this.cargados = this.resolucion.filter((r) => r.estado === ESTADOS.CARGADO).length;
        this.fallidas = this.resolucion.filter((r) => r.estado === ESTADOS.ERROR).length;

        Swal.fire({
          icon: this.fallidas > 0 ? "warning" : "success",
          title: this.fallidas > 0 ? "Carga parcial" : "Carga exitosa",
          text: `${this.cargados} planilla(s) cargada(s) correctamente${this.fallidas ? `, ${this.fallidas} con error` : ""}.`,
          confirmButtonColor: "#0d6efd",
        });

        this.$emit("ejecutado");
        this.$emit("cargado");
      } finally {
        this.procesando = false;
      }
    },

    // ---------- UI helpers ----------
    claseEstado(estado) {
      return {
        [ESTADOS.NO_ENCONTRADO]: "text-danger",
        [ESTADOS.ERROR]: "text-danger",
        [ESTADOS.CARGADO]: "text-success",
        [ESTADOS.CARGANDO]: "text-primary",
      }[estado] || "text-muted";
    },
    etiquetaEstado(estado) {
      return {
        [ESTADOS.RESUELTO]: "✔ resuelto",
        [ESTADOS.NO_ENCONTRADO]: "✘ no encontrado",
        [ESTADOS.CARGANDO]: "⏳ cargando...",
        [ESTADOS.CARGADO]: "✔ cargado",
        [ESTADOS.ERROR]: "✘ error",
      }[estado] || estado;
    },

    limpiar() {
      this.archivoCsv = null;
      this.numerosCsv = [];
      this.pdfs = [];
      this.resolucion = [];
      this.cargados = null;
      this.fallidas = null;
      this.progresoActual = 0;
      if (this.$refs.inputCsv) this.$refs.inputCsv.value = "";
      if (this.$refs.inputPdf) this.$refs.inputPdf.value = "";
    },
  },
};
</script>

<style scoped>
.reg-panel  { background: #f8f9fa; }
.panel-body { padding: 14px 16px; }
.search-input {
  width: 100%; padding: 6px 10px; border: 1px solid #ced4da;
  border-radius: 4px; font-size: 0.85rem; transition: border-color 0.15s;
}
.search-input:focus { outline: none; border-color: #0d6efd; box-shadow: 0 0 0 2px rgba(13,110,253,.15); }
.btn-reload {
  display: flex; align-items: center; justify-content: center;
  padding: 6px 10px; border: 1px solid #ced4da; border-radius: 4px;
  background: #fff; cursor: pointer; color: #495057; transition: all 0.15s;
}
.btn-reload:hover { background: #0d6efd; color: #fff; border-color: #0d6efd; }
.search-status { font-size: 0.78rem; }
.field-label { font-weight: 600; font-size: 0.85rem; color: #495057; display: block; margin-bottom: 4px; }
.tabla-seleccionados {
  border: 1px solid #dee2e6; border-radius: 6px; overflow: hidden; font-size: 0.82rem;
}
.tabla-header {
  display: grid;
  background: #e9ecef; padding: 5px 8px; font-weight: 700; color: #495057; gap: 6px;
}
.log-box {
  max-height: 320px; overflow-y: auto; background: #fff;
  font-family: "Courier New", monospace; font-size: 0.76rem;
}
.log-line {
  padding: 3px 8px; border-top: 1px solid #f0f0f0; white-space: pre-wrap;
}
.tabla-vacia {
  display: flex; flex-direction: column; align-items: center;
  padding: 20px; color: #adb5bd; font-size: 0.85rem;
  border: 2px dashed #dee2e6; border-radius: 6px; background: #fff;
}
.resumen-box {
  background: #fff; border: 1px solid #dee2e6;
  border-radius: 6px; padding: 10px 12px; font-size: 0.82rem;
}
.resumen-row {
  display: flex; justify-content: space-between; padding: 2px 0; color: #495057;
}
.btn-action {
  display: flex; align-items: center; justify-content: center; gap: 6px;
  padding: 8px 14px; border: none; border-radius: 6px;
  font-size: 0.85rem; font-weight: 600; cursor: pointer;
  transition: all 0.15s; width: 100%;
}
.btn-action.primary                      { background: #0d6efd; color: #fff; }
.btn-action.primary:hover:not(:disabled) { background: #0b5ed7; }
.btn-action.secondary                    { background: #fff; color: #6c757d; border: 1px solid #dee2e6; }
.btn-action.secondary:hover:not(:disabled) { background: #f8f9fa; }
.btn-action:disabled                     { opacity: 0.45; cursor: not-allowed; }

.lista-pdfs { display: flex; flex-wrap: wrap; gap: 6px; }
.pdf-chip {
  display: inline-flex; align-items: center; gap: 4px;
  background: #fff0f0; color: #b02a37; border: 1px solid #f5c2c7;
  border-radius: 20px; padding: 3px 8px; font-size: 0.75rem;
}
.pdf-chip-quitar {
  cursor: pointer; font-weight: 700; margin-left: 2px;
  padding: 0 3px; border-radius: 50%;
}
.pdf-chip-quitar:hover { background: #dc3545; color: #fff; }
</style>